import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { queue } from "@/components/Toast";
import type { ChatSession } from "@/types/chat.type";

export const agentSessionsKey = (brandId: string | null) => [
  "brandAgentSessions",
  brandId,
];

/** One chat list per brand — the same conversations whatever page you are on. */
export function useBrandAgentSessions(brandId: string) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: agentSessionsKey(brandId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ sessions: ChatSession[] }>(
        `/brand-agent/${brandId}/sessions`,
      );
      return res.data.sessions;
    },
    enabled: !!brandId,
  });

  const createSession = useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post<ChatSession>(
        `/brand-agent/${brandId}/sessions`,
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: agentSessionsKey(brandId) }),
  });

  // Optimistic: the row and the rail head take the new name at once; a failure
  // puts back only this chat's old name.
  const renameSession = useMutation({
    mutationFn: async (vars: { sessionId: string; title: string }) => {
      await axiosInstance.patch(
        `/brand-agent/${brandId}/sessions/${vars.sessionId}`,
        { title: vars.title },
      );
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: agentSessionsKey(brandId) });
      const previous = qc
        .getQueryData<ChatSession[]>(agentSessionsKey(brandId))
        ?.find((s) => s.id === vars.sessionId)?.title;
      qc.setQueryData<ChatSession[]>(agentSessionsKey(brandId), (list) =>
        list?.map((s) => (s.id === vars.sessionId ? { ...s, title: vars.title } : s)),
      );
      return { previous };
    },
    onError: (_err, vars, context) => {
      if (context?.previous !== undefined) {
        qc.setQueryData<ChatSession[]>(agentSessionsKey(brandId), (list) =>
          list?.map((s) => (s.id === vars.sessionId ? { ...s, title: context.previous! } : s)),
        );
      }
      queue.add({ title: "Could not rename the chat", description: "Please try again.", variant: "error" });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: agentSessionsKey(brandId) }),
  });

  const deleteSession = useMutation({
    mutationFn: async (sessionId: string) => {
      await axiosInstance.delete(`/brand-agent/${brandId}/sessions/${sessionId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: agentSessionsKey(brandId) }),
  });

  return {
    sessions: query.data ?? [],
    isLoading: query.isLoading,
    createSession,
    renameSession,
    deleteSession,
    invalidateSessions: () =>
      qc.invalidateQueries({ queryKey: agentSessionsKey(brandId) }),
  };
}
